import { useMutation, useQuery } from '@apollo/client';
import * as React from 'react';

import { ChecklistSection, ReleasePreviewStatus } from '../../__generated__/globalTypes';
import Header from '../../components/Header';
import Table from '../../components/Table';
import { useToast } from '../../components/Toast';
import { IS_DEV } from '../../env';
import { useAuthState } from '../../providers/AuthProvider';

import CurrentDraftCard from './components/CurrentDraftCard';
import HistoryRow from './components/HistoryRow';
import PendingReleaseCard from './components/PendingReleaseCard';
import {
  deriveAnnounceButtonState,
  deriveStartDraftButtonState,
  formatChecklistMarker,
  isMultipleDraftsError,
  mapDraftMutationError,
  releaseBenchmarkGate,
  RELEASE_DRAFT_POLL_INTERVAL_MS,
  sha256Hex,
  shouldAutoRunPreview,
} from './releaseLogic';
import {
  ADMIN_ANNOUNCE_PRE_RELEASE,
  ADMIN_DELETE_RELEASE_DRAFT,
  ADMIN_GET_ASSISTANT_BENCHMARK_FOR_HEAD,
  ADMIN_GET_CURRENT_RELEASE_DRAFT,
  ADMIN_GET_RELEASE_CHANGE_LOG,
  ADMIN_GET_RELEASE_PREVIEW,
  ADMIN_LIST_RELEASES,
  ADMIN_PUBLISH_RELEASE_DRAFT,
  ADMIN_START_RELEASE_PREVIEW,
  ADMIN_TOGGLE_RELEASE_CHECKLIST_ITEM,
} from './releaseOperations';

import type {
  AdminGetAssistantBenchmarkForHeadQuery,
  AdminGetAssistantBenchmarkForHeadQueryVariables,
  AdminGetReleaseChangeLogQuery,
  AdminGetReleasePreviewQuery,
  AdminGetReleasePreviewQueryVariables,
  AdminStartReleasePreviewMutation,
  AdminStartReleasePreviewMutationVariables,
  AdminGetCurrentReleaseDraftQuery,
  AdminListReleasesQuery,
  AdminAnnouncePreReleaseMutation,
  AdminAnnouncePreReleaseMutationVariables,
  AdminPublishReleaseDraftMutation,
  AdminPublishReleaseDraftMutationVariables,
  AdminDeleteReleaseDraftMutation,
  AdminDeleteReleaseDraftMutationVariables,
  AdminToggleReleaseChecklistItemMutation,
  AdminToggleReleaseChecklistItemMutationVariables,
} from './__generated__/releaseOperations.gql';
import type { IAnnounceJustPosted } from './releaseLogic';

/**
 * Mirrors the env gate in `@orn/mops-api`'s `SlackReleaseNotifier` —
 * dev posts to `#production-deploys-test` so an admin exercising the
 * tool against their dev box cannot page on-call. Staging and prod
 * both target the real channel.
 */
const SLACK_CHANNEL = IS_DEV ? '#production-deploys-test' : '#production-deploys';

/**
 * Empirically, GitHub's `listReleases` reflects a freshly-created
 * draft within a few seconds of `createRelease`. 10s is a generous
 * margin that has been reliable in staging; if we ever observe
 * timeouts longer than this, switch to a polling loop with a cap.
 */
const DRAFT_PROPAGATION_DELAY_MS = 10_000;

/** Apollo poll cadence for `adminGetReleasePreview` (cache-status check). */
const PREVIEW_POLL_INTERVAL_MS = 3_000;
/**
 * Hard ceiling on how long we'll keep polling for the LLM cache row to
 * appear. After this, we surface "still generating, refresh to retry"
 * rather than silently re-querying for hours on a forgotten tab. The
 * worker reliably writes the cache row in 30–60 s; 90 s gives ample
 * margin for outliers without burning the operator's tab indefinitely.
 */
const PREVIEW_POLL_CAP_MS = 90_000;

/**
 * Poll cadence for the open draft's benchmark. Deliberately slower than the
 * Benchmarks tool's own detail poll: every poll reconciles against GitHub, and a
 * whole-suite run lasts 30-60 minutes with nobody watching it frame by frame.
 */
const RELEASE_BENCHMARK_POLL_INTERVAL_MS = 20_000;

/**
 * Re-check of the pending changelog while the operator sits on the idle screen.
 * `main` moves under a tab left open, and the commit list, its per-commit CI, and
 * the commit the draft would be pinned to all move with it.
 *
 * A minute rather than something snappier because the server resolves CI one
 * commit at a time — GitHub has no bulk check-runs endpoint — so each fetch is
 * up to ~20 API calls against an hourly installation limit shared with every
 * other GitHub-backed MOPS tool.
 */
const CHANGE_LOG_POLL_INTERVAL_MS = 60_000;

/**
 * Releases admin tool. Three stacked sections:
 *
 *  1. **Draft card** — surfaces the currently-open MOPS draft on GitHub,
 *     if any. Wires `adminPublishReleaseDraft` and `adminDeleteReleaseDraft`
 *     behind two danger buttons + two confirmation modals, gated by an
 *     interactive checklist over the LLM-generated `prerequisites` and
 *     `testPlan` (see `ReleaseChecklistGate`). Auto-fires the preview
 *     query when a draft is present so the checklist data is available.
 *  2. **Pending release** — preview of what `baseTag → headSha` would tag as,
 *     including the LLM summary, risk callouts, prerequisites checklist,
 *     suggested test plan, CI status, and the merged commits since the base
 *     tag. The "Announce pre-release" button creates the GitHub draft + posts
 *     Slack; the button's exact label and enable/disable state is derived
 *     from the open-draft state in `releaseLogic.deriveAnnounceButtonState`.
 *  3. **Release history** — recent GitHub releases. MOPS-authored releases
 *     show the operator email parsed from the audit footer; legacy
 *     `release.yml` releases show `—`.
 */
const ReleasesScene = () => {
  const { showToast } = useToast();

  // Release-notes preview now runs as a `start mutation + polling query`
  // pair so the slow Anthropic call (30-60 s) runs off the synchronous
  // request path on `@orn/api`'s `_async` worker — `@orn/mops-api`'s 30 s
  // API Gateway timeout never sees it. The mutation snapshots a
  // `(baseSha, headSha)` pair; the query polls the LLM cache for that
  // pair every 3 s until the worker writes the row.
  const [pollingTimedOut, setPollingTimedOut] = React.useState(false);
  /**
   * Commit the in-flight generation is pinned to, or `null` for current `main`
   * HEAD. Held so Retry and the post-mutation refresh re-run at the same pin
   * instead of silently sliding forward to whatever HEAD has become.
   */
  const [pinnedHeadSha, setPinnedHeadSha] = React.useState<string | null>(null);
  const [startedShas, setStartedShas] = React.useState<{ baseSha: string; headSha: string } | null>(null);
  const [startedState, setStartedState] = React.useState<
    AdminStartReleasePreviewMutation['adminStartReleasePreview'] | null
  >(null);

  const [startReleasePreview, { error: startError }] = useMutation<
    AdminStartReleasePreviewMutation,
    AdminStartReleasePreviewMutationVariables
  >(ADMIN_START_RELEASE_PREVIEW);

  const {
    data: pollData,
    error: pollError,
    stopPolling,
  } = useQuery<AdminGetReleasePreviewQuery, AdminGetReleasePreviewQueryVariables>(ADMIN_GET_RELEASE_PREVIEW, {
    variables: startedShas ?? { baseSha: '', headSha: '' },
    // `skip` keeps Apollo from issuing requests pre-Start. Polling stops
    // once we transition to COMPLETED via the effect below — both halt
    // mechanisms are coordinated so a stale poll doesn't fire after the
    // operator has already moved on.
    skip: !startedShas || pollingTimedOut,
    pollInterval: PREVIEW_POLL_INTERVAL_MS,
    notifyOnNetworkStatusChange: false,
  });
  const polledStatus = pollData?.adminGetReleasePreview?.status ?? null;

  // Stop polling as soon as a COMPLETED row arrives. Apollo's poll loop
  // doesn't auto-stop on data shape; we trip it imperatively here so the
  // tab doesn't keep hitting `/graphql` after the operator has the
  // preview rendered.
  React.useEffect(() => {
    if (polledStatus === ReleasePreviewStatus.Completed) {
      stopPolling();
    }
  }, [polledStatus, stopPolling]);

  // 90s polling cap: if the worker hasn't written the cache row by then,
  // the UI flips to a "still generating, refresh to check" state rather
  // than polling forever on a forgotten tab.
  React.useEffect(() => {
    if (!startedShas) {
      return undefined;
    }
    if (startedState?.status === ReleasePreviewStatus.Completed || polledStatus === ReleasePreviewStatus.Completed) {
      return undefined;
    }
    const timer = setTimeout(() => setPollingTimedOut(true), PREVIEW_POLL_CAP_MS);
    return () => clearTimeout(timer);
  }, [startedShas, startedState?.status, polledStatus]);

  /**
   * Kicks off (or restarts) the preview-generation flow at `headSha` — the
   * operator's pin, or `null` to cut at current `main` HEAD. Resets the timeout
   * and the start-mutation snapshot so a Retry click swaps the scene back
   * to the spinner immediately. Apollo's errorLink reports failures to
   * Sentry; the inline error UI keys off `startError` / `pollError` /
   * `pollingTimedOut`.
   */
  const runPreview = React.useCallback(
    async (headSha: string | null): Promise<void> => {
      setPollingTimedOut(false);
      setStartedShas(null);
      setStartedState(null);
      setPinnedHeadSha(headSha);
      try {
        const result = await startReleasePreview({ variables: { headSha } });
        const state = result.data?.adminStartReleasePreview;
        if (!state) {
          return;
        }
        setStartedState(state);
        setStartedShas({ baseSha: state.baseSha, headSha: state.headSha });
      } catch {
        // ApolloProvider's errorLink reports to Sentry. `startedShas` stays null
        // here, so the scene remains on the idle screen rather than swapping to
        // the preview's error panel — that screen renders `startError` itself.
      }
    },
    [startReleasePreview]
  );

  /** Re-runs generation at the pin already in play — Retry, and the post-announce refresh. */
  const refetchPreview = React.useCallback((): Promise<void> => runPreview(pinnedHeadSha), [runPreview, pinnedHeadSha]);

  /**
   * Drops the generated preview and its pin, returning the scene to the idle
   * screen so the changelog query reloads from whatever the repository now is.
   *
   * Publishing is the one transition a re-run cannot survive: the pinned commit
   * becomes the new release's base tag, so regenerating at it either drifts (the
   * pin is now the compare base and no longer among the commits) or generates
   * notes for an empty diff. Clearing the preview also re-arms the auto-trigger
   * above, so publishing latches `publishedTargetSha` to keep it from firing.
   */
  const resetPreview = React.useCallback((): void => {
    setPollingTimedOut(false);
    setStartedShas(null);
    setStartedState(null);
    setPinnedHeadSha(null);
  }, []);

  const previewState = pollData?.adminGetReleasePreview ?? startedState;
  const previewCalled = startedShas !== null;
  const previewLoading =
    previewCalled &&
    previewState?.status !== ReleasePreviewStatus.Completed &&
    !pollingTimedOut &&
    !pollError &&
    !startError;
  const previewError = startError ?? pollError ?? null;

  const {
    data: draftData,
    loading: draftLoading,
    refetch: refetchDraft,
  } = useQuery<AdminGetCurrentReleaseDraftQuery>(ADMIN_GET_CURRENT_RELEASE_DRAFT, {
    // Cross-tab sync: another release manager's toggle becomes visible
    // here within ~30s without a push channel. `notifyOnNetworkStatusChange`
    // stays off so the silent polls don't flicker any loading state.
    pollInterval: RELEASE_DRAFT_POLL_INTERVAL_MS,
    notifyOnNetworkStatusChange: false,
  });

  const openDraftTargetSha = draftData?.adminGetCurrentReleaseDraft?.targetSha ?? null;

  // The lightweight changelog runs on mount — it doesn't trigger the LLM,
  // just a few GitHub round-trips, so the page can show operators what's
  // pending before they commit to starting a draft. Skipped once a draft is
  // open: only the idle screen reads it, so polling on would keep hitting
  // GitHub on every poll for data nothing renders.
  //
  // `network-only` because this is what gates Start on CI, and nothing re-checks
  // CI before a draft exists — the server only enforces it at publish. Under the
  // default cache-first, discarding a draft would replay whatever CI status was
  // cached before it was announced, so a since-broken HEAD would read green.
  const {
    data: changeLogData,
    loading: changeLogLoading,
    error: changeLogError,
  } = useQuery<AdminGetReleaseChangeLogQuery>(ADMIN_GET_RELEASE_CHANGE_LOG, {
    skip: openDraftTargetSha !== null,
    fetchPolicy: 'network-only',
    pollInterval: CHANGE_LOG_POLL_INTERVAL_MS,
    notifyOnNetworkStatusChange: false,
  });

  const {
    data: historyData,
    loading: historyLoading,
    refetch: refetchHistory,
  } = useQuery<AdminListReleasesQuery>(ADMIN_LIST_RELEASES);

  // The draft's benchmark. Skipped unless a draft is open — the pending-release
  // preview has no run to show, and an unskipped query at a head with no run would
  // poll forever, since a `null` result never reaches the terminal stop below.
  const {
    data: benchmarkData,
    loading: benchmarkLoading,
    error: benchmarkError,
    stopPolling: stopBenchmarkPolling,
  } = useQuery<AdminGetAssistantBenchmarkForHeadQuery, AdminGetAssistantBenchmarkForHeadQueryVariables>(
    ADMIN_GET_ASSISTANT_BENCHMARK_FOR_HEAD,
    {
      variables: { headSha: openDraftTargetSha ?? '' },
      skip: openDraftTargetSha === null,
      pollInterval: RELEASE_BENCHMARK_POLL_INTERVAL_MS,
      notifyOnNetworkStatusChange: false,
    }
  );
  const benchmarkRun = benchmarkData?.adminGetAssistantBenchmarkForHead ?? null;
  const benchmarkGate = releaseBenchmarkGate(benchmarkRun, benchmarkLoading, Boolean(benchmarkError));

  // Each poll reconciles the run against GitHub, so stop once it settles rather
  // than keep paying for a result that can no longer change.
  React.useEffect(() => {
    if (benchmarkGate === 'settled') {
      stopBenchmarkPolling();
    }
  }, [benchmarkGate, stopBenchmarkPolling]);

  const [announce, { loading: announceLoading }] = useMutation<
    AdminAnnouncePreReleaseMutation,
    AdminAnnouncePreReleaseMutationVariables
  >(ADMIN_ANNOUNCE_PRE_RELEASE);

  const [publishDraft] = useMutation<AdminPublishReleaseDraftMutation, AdminPublishReleaseDraftMutationVariables>(
    ADMIN_PUBLISH_RELEASE_DRAFT
  );

  const [deleteDraft] = useMutation<AdminDeleteReleaseDraftMutation, AdminDeleteReleaseDraftMutationVariables>(
    ADMIN_DELETE_RELEASE_DRAFT
  );

  const [toggleChecklistItem] = useMutation<
    AdminToggleReleaseChecklistItemMutation,
    AdminToggleReleaseChecklistItemMutationVariables
  >(ADMIN_TOGGLE_RELEASE_CHECKLIST_ITEM);

  // Operator's email — used to format the optimistic checklist marker
  // ("Checked by **localPart** at …") so the UI shows the attribution
  // the same frame the click lands. The server re-formats from
  // `vc.user.email` and the strings match byte-for-byte.
  const { user } = useAuthState();
  const operatorEmail = user?.email ?? '';

  const [justPosted, setJustPosted] = React.useState<IAnnounceJustPosted | null>(null);
  /**
   * Latched when the publish or delete mutation rejects with
   * `MULTIPLE_DRAFTS_FOUND`. Drives the recovery banner on the draft
   * card and disables Publish until the operator walks the queue down
   * via Discard. Cleared on every successful mutation or refetch.
   */
  const [multipleDraftsDetected, setMultipleDraftsDetected] = React.useState(false);
  /**
   * True between the announce mutation resolving and the post-delay
   * refetch landing. Keeps the Announce button visually pinned in its
   * loading state through GitHub's read-after-write window so the
   * operator can't double-click while we're waiting for the draft to
   * surface in `listReleases`.
   */
  const [isWaitingForDraftPropagation, setIsWaitingForDraftPropagation] = React.useState(false);
  /**
   * Target SHA of the draft this session just published. Publishing makes that
   * commit the *next* release's compare base, so regenerating a preview at it is
   * rejected server-side — and the auto-trigger below would do exactly that,
   * because clearing the preview re-arms it while the published draft is still
   * in Apollo's cache. Cleared when a new draft is announced.
   */
  const [publishedTargetSha, setPublishedTargetSha] = React.useState<string | null>(null);

  const preview = previewState?.preview ?? null;
  const changeLog = changeLogData?.adminGetReleaseChangeLog;
  const draft = draftData?.adminGetCurrentReleaseDraft ?? null;
  const history = historyData?.adminListReleases ?? [];

  // Auto-trigger preview when a draft is open. The interactive
  // checklist gate on the draft card needs the structured prereqs and
  // testPlan from `adminGetReleasePreview` — the LLM cache hits because
  // the draft was just announced (or is being viewed shortly after),
  // so this typically lands instantly. If the operator refreshes hours
  // later and the cache has been evicted, they pay the 30-60s
  // regeneration cost; the gate shows "Loading checklist…" in the
  // meantime and Publish stays disabled.
  React.useEffect(() => {
    if (draft && shouldAutoRunPreview({ draftTargetSha: draft.targetSha, publishedTargetSha, previewCalled })) {
      // Pinned to the draft's own head, not current `main`. The draft was cut at
      // the newest green commit and `main` keeps moving, so regenerating at HEAD
      // would both miss the cache row the announce already wrote and hand the
      // gate a checklist for a diff nobody is releasing.
      void runPreview(draft.targetSha);
    }
  }, [draft, publishedTargetSha, previewCalled, runPreview]);

  const buttonState = preview
    ? deriveAnnounceButtonState({
        expectedHeadSha: preview.expectedHeadSha,
        draftTargetSha: draft?.targetSha ?? null,
        slackChannel: SLACK_CHANNEL,
      })
    : null;

  // Starting a draft commits to a head commit, so the server picks the newest one
  // CI has blessed and the button reports which. A failed poll folds into
  // unavailable rather than being ignored: Apollo keeps the last good result on
  // error, and trusting that would pin against a CI status we no longer know to
  // be current. Loading and unavailable stay mutually exclusive so the first
  // fetch reads as "checking", not "unreachable".
  const changeLogIsLoading = changeLogLoading && !changeLog;
  const startDraftButtonState = deriveStartDraftButtonState({
    changeLogLoading: changeLogIsLoading,
    changeLogUnavailable: !changeLogIsLoading && (!changeLog || Boolean(changeLogError)),
    ciConclusion: changeLog?.ciStatus.conclusion ?? 'unknown',
    commits: changeLog?.commits ?? [],
    releasableHeadSha: changeLog?.releasableHeadSha ?? null,
  });

  const handleAnnounce = async () => {
    if (!preview || !buttonState || buttonState.mode === 'disabled') {
      return;
    }
    try {
      const result = await announce({ variables: { expectedHeadSha: preview.expectedHeadSha } });
      const data = result.data?.adminAnnouncePreRelease;
      setJustPosted({ postedAt: new Date(), slackUrl: data?.slackMessageUrl ?? null });
      setPublishedTargetSha(null);
      // `handleAnnounce` is only mounted when no draft is open, so
      // `buttonState.mode` is always `'announce'` here. The `'repost-slack'`
      // mode is handled by `CurrentDraftCard` calling `handleRepostSlack`.
      showToast({ type: 'success', message: `Draft ${data?.draftTag} announced.` });
      // GitHub's `listReleases` lags `createRelease` by a few seconds.
      // Refetching immediately race-conditions with that window and
      // returns null for the brand-new draft, leaving the operator
      // stuck on the pending-release preview. Pause for the propagation
      // window before refetching so the first refetch surfaces the
      // draft and the page swaps to `CurrentDraftCard`.
      setIsWaitingForDraftPropagation(true);
      await new Promise((resolve) => setTimeout(resolve, DRAFT_PROPAGATION_DELAY_MS));
      // The preview re-runs at the same pin, so a subsequent announce after the
      // operator backs out of the draft re-announces the same commit rather than
      // sliding forward to whatever `main` has become. Guarded by `previewCalled` because Apollo's `useLazyQuery.refetch`
      // throws "Refetch called on a query that has not been executed" if
      // invoked before the first `runPreview()`. Today this branch is
      // reached only when `preview` is non-null (so `previewCalled` is
      // also true), but the explicit guard means a future refactor that
      // shifts how this handler mounts can't silently regress into a
      // throw-on-refetch.
      await Promise.all([refetchDraft(), refetchHistory(), previewCalled ? refetchPreview() : Promise.resolve()]);
      setIsWaitingForDraftPropagation(false);
    } catch {
      // ApolloProvider's errorLink reports GraphQL + network errors to Sentry.
      setIsWaitingForDraftPropagation(false);
      showToast({ type: 'error', message: 'Failed to announce pre-release. Please try again.' });
    }
  };

  /**
   * Re-post the Slack announcement for the open draft. Goes through the
   * same `announcePreRelease` mutation as the initial announce — the
   * server treats a second call at the same `targetSha` as idempotent
   * (skips creating a duplicate draft, just re-posts Slack).
   */
  const handleRepostSlack = async () => {
    if (!draft) {
      return;
    }
    try {
      const result = await announce({ variables: { expectedHeadSha: draft.targetSha } });
      const data = result.data?.adminAnnouncePreRelease;
      setJustPosted({ postedAt: new Date(), slackUrl: data?.slackMessageUrl ?? null });
      showToast({ type: 'success', message: 'Re-posted to Slack.' });
      await refetchDraft();
    } catch {
      showToast({ type: 'error', message: 'Failed to re-post Slack. Please try again.' });
    }
  };

  /**
   * Publishes the open draft. Server validates `expectedDraftTag`,
   * re-checks CI at `target_commitish`, then promotes the draft on
   * GitHub (which triggers `release.yml` → prod deploy). Server errors
   * map to operator-facing toast copy via `mapDraftMutationError`.
   *
   * Throws on failure so `DangerConfirmDialog` keeps itself open and
   * preserves the typed-confirmation value, letting the operator retry
   * without re-typing the tag.
   */
  const handlePublishDraft = async () => {
    if (!draft) {
      return;
    }
    try {
      await publishDraft({ variables: { expectedDraftTag: draft.tag } });
      setMultipleDraftsDetected(false);
      showToast({ type: 'success', message: `Published ${draft.tag}.` });
      // The reset below re-arms the auto-trigger while the draft query still
      // answers with the draft that just shipped; the latch is what holds it.
      setPublishedTargetSha(draft.targetSha);
      resetPreview();
      await Promise.all([refetchDraft(), refetchHistory()]);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      if (isMultipleDraftsError(message)) {
        setMultipleDraftsDetected(true);
      }
      showToast({ type: 'error', message: mapDraftMutationError(message) });
      throw error;
    }
  };

  /**
   * Toggle a single checklist item. Apollo writes the mutation response
   * (the canonical `ReleaseDraft` shape, including refreshed checked-index
   * arrays) into cache via the default behavior, so the UI updates without
   * a follow-up fetch in this tab. Other tabs pick up the change on the
   * next 30s poll.
   *
   * `optimisticResponse` synthesizes a tentative `ReleaseDraft` with the
   * targeted index toggled and the marker text formatted client-side
   * (using the current operator's email + `now`) so the "Checked by …"
   * line appears the same frame the click lands. The marker formatter
   * mirrors the server's byte-for-byte, so when the real response
   * arrives a moment later the visible marker doesn't flicker.
   *
   * Errors map through `mapDraftMutationError` to operator-facing toast
   * copy. `STALE_CHECKLIST` triggers a draft refetch so the operator's
   * next click sees the up-to-date body.
   */
  const handleToggleChecklistItem = React.useCallback(
    async (section: ChecklistSection, index: number, checked: boolean, lineContent: string) => {
      if (!draft) {
        return;
      }
      try {
        const expectedLineHash = await sha256Hex(lineContent);
        const operatorLocalPart = operatorEmail.split('@')[0] || 'unknown';
        const isPrereq = section === ChecklistSection.Prerequisite;
        const indices = isPrereq ? draft.prerequisiteCheckedIndices : draft.testPlanCheckedIndices;
        const markers = isPrereq ? draft.prerequisiteCheckedMarkers : draft.testPlanCheckedMarkers;
        const nextIndices = checked
          ? Array.from(new Set([...indices, index])).sort((a, b) => a - b)
          : indices.filter((i) => i !== index);
        const nextMarkers = markers.map((m, i) =>
          i === index ? (checked ? formatChecklistMarker(operatorLocalPart, new Date()) : '') : m
        );
        const optimisticDraft: AdminToggleReleaseChecklistItemMutation['adminToggleReleaseChecklistItem'] = {
          ...draft,
          prerequisiteCheckedIndices: isPrereq ? nextIndices : draft.prerequisiteCheckedIndices,
          testPlanCheckedIndices: isPrereq ? draft.testPlanCheckedIndices : nextIndices,
          prerequisiteCheckedMarkers: isPrereq ? nextMarkers : draft.prerequisiteCheckedMarkers,
          testPlanCheckedMarkers: isPrereq ? draft.testPlanCheckedMarkers : nextMarkers,
        };
        await toggleChecklistItem({
          variables: { expectedDraftTag: draft.tag, section, index, checked, expectedLineHash },
          optimisticResponse: {
            __typename: 'Mutation',
            adminToggleReleaseChecklistItem: optimisticDraft,
          },
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        showToast({ type: 'error', message: mapDraftMutationError(message) });
        // Always refetch on error so the gate's optimistic-intent overlay
        // reconciles against the canonical body. Without this, a
        // GITHUB_UPDATE_FAILED leaves the box visually flipped until the
        // next 30s poll lands.
        await refetchDraft();
      }
    },
    [draft, toggleChecklistItem, showToast, refetchDraft, operatorEmail]
  );

  /**
   * Discards the open draft. Targets the front-of-queue draft on the
   * server side; the operator clicks again until only one remains
   * during MULTIPLE_DRAFTS_FOUND recovery. Refetches the draft query
   * to surface the next state.
   */
  const handleDiscardDraft = async () => {
    if (!draft) {
      return;
    }
    try {
      await deleteDraft({ variables: { expectedDraftTag: draft.tag } });
      setMultipleDraftsDetected(false);
      showToast({ type: 'success', message: `Discarded ${draft.tag}.` });
      await refetchDraft();
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      if (isMultipleDraftsError(message)) {
        setMultipleDraftsDetected(true);
      }
      showToast({ type: 'error', message: mapDraftMutationError(message) });
      throw error;
    }
  };

  return (
    <div className="bg-bg-primary text-fg-primary flex min-h-screen flex-col">
      <Header />
      <main className="flex flex-1 flex-col gap-6 px-6 py-5">
        <h1 className="text-sm font-semibold uppercase tracking-widest">Releases</h1>

        {/*
          When a MOPS draft is open, the **draft** is the actionable state —
          the preview at HEAD is no longer the operator's decision point.
          Show the Current Draft card and hide the Pending release preview;
          when no draft is open, show the preview as today.
        */}
        {draft ? (
          <CurrentDraftCard
            draft={draft}
            onRepostSlack={() => void handleRepostSlack()}
            isReposting={announceLoading}
            justPosted={justPosted}
            preview={preview ?? null}
            previewLoading={previewLoading}
            previewError={Boolean(previewError)}
            multipleDraftsDetected={multipleDraftsDetected}
            benchmarkRun={benchmarkRun}
            benchmarkLoading={benchmarkLoading}
            benchmarkError={Boolean(benchmarkError)}
            benchmarkPollIntervalMs={RELEASE_BENCHMARK_POLL_INTERVAL_MS}
            benchmarkGate={benchmarkGate}
            onPublishConfirmed={handlePublishDraft}
            onDiscardConfirmed={handleDiscardDraft}
            onToggleChecklistItem={(section, index, checked, lineContent) =>
              void handleToggleChecklistItem(section, index, checked, lineContent)
            }
          />
        ) : (
          <PendingReleaseCard
            preview={preview}
            previewCalled={previewCalled}
            previewLoading={previewLoading}
            previewError={previewError}
            pollingTimedOut={pollingTimedOut}
            changeLog={changeLog ?? null}
            changeLogLoading={changeLogLoading}
            startDraftButtonState={startDraftButtonState}
            buttonState={buttonState}
            justPosted={justPosted}
            announceLoading={announceLoading}
            isWaitingForDraftPropagation={isWaitingForDraftPropagation}
            draftLoading={draftLoading}
            slackChannel={SLACK_CHANNEL}
            onStartDraft={() => void runPreview(startDraftButtonState.pinnedSha)}
            onRetry={() => void refetchPreview()}
            onAnnounce={() => void handleAnnounce()}
          />
        )}

        {/* Release history */}
        <section aria-label="Release history" className="flex flex-col gap-2">
          <h2 className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">Release history</h2>
          {historyLoading && history.length === 0 ? (
            <span className="text-fg-secondary text-xs">Loading…</span>
          ) : history.length === 0 ? (
            <span className="text-fg-tertiary text-xs italic">No releases yet.</span>
          ) : (
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Tag</Table.HeaderCell>
                  <Table.HeaderCell>Date</Table.HeaderCell>
                  <Table.HeaderCell>Drafted by</Table.HeaderCell>
                  <Table.HeaderCell>Summary</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {history.map((release) => (
                  <HistoryRow key={release.tag} release={release} />
                ))}
              </Table.Body>
            </Table>
          )}
        </section>
      </main>
    </div>
  );
};

export default ReleasesScene;
